import React, { useState, useEffect } from 'react';
import api from '../services/api';
import ClosingReport from './ClosingReport';
import { Image as ImageIcon, X } from 'lucide-react';

const SessionDetailsModal = ({ sessionId, onClose }) => {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showReport, setShowReport] = useState(false);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const response = await api.get(`/cash-box/sessions/${sessionId}`);
                setDetails(response.data);
            } catch (error) {
                console.error('Error fetching session details:', error);
            } finally {
                setLoading(false);
            }
        };

        if (sessionId) {
            fetchDetails();
        }
    }, [sessionId]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.round(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('es-CO');
    };

    if (!sessionId) return null;

    const handleExport = async () => {
        try {
            const response = await api.get(`/cash-box/sessions/${sessionId}/export`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `reporte_caja_${sessionId}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting CSV:', error);
            alert('Error al exportar CSV');
        }
    };

    const handleExportPDF = async () => {
        try {
            const response = await api.get(`/cash-box/sessions/${sessionId}/export-pdf`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `reporte_caja_${sessionId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert('Error al exportar PDF');
        }
    };

    if (showReport && details) {
        return (
            <ClosingReport
                session={details.session}
                movements={details.movements}
                onClose={() => setShowReport(false)}
            />
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">
                            Detalle de Sesión #{sessionId}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {loading ? 'Cargando...' :
                                `${details?.session?.box_name} • ${formatDate(details?.session?.opened_at)}`}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="text-center py-10">Cargando detalles...</div>
                    ) : details ? (
                        <div className="space-y-8">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                    <div className="text-sm text-blue-800 font-medium">Base Inicial</div>
                                    <div className="text-2xl font-bold text-blue-900">
                                        {formatCurrency(details.session.opening_balance)}
                                    </div>
                                    <div className="text-xs text-blue-600 mt-1">
                                        Por: {details.session.opened_by_name}
                                    </div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                                    <div className="text-sm text-green-800 font-medium">Total Ingresos</div>
                                    <div className="text-2xl font-bold text-green-900">
                                        {formatCurrency(details.session.calculated_total_income || 0)}
                                    </div>
                                </div>
                                <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                                    <div className="text-sm text-red-800 font-medium">Total Egresos</div>
                                    <div className="text-2xl font-bold text-red-900">
                                        {formatCurrency(details.session.calculated_total_expenses || 0)}
                                    </div>
                                </div>
                            </div>

                            {/* Route Deductions */}
                            {details.route_deductions && details.route_deductions.length > 0 && (
                                <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                                    <h3 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
                                        <span>🚛</span> Deducciones de Ruta
                                        <span className="text-sm font-normal text-orange-600">
                                            (Total: {formatCurrency(details.session.route_deductions_total || 0)})
                                        </span>
                                    </h3>
                                    <div className="space-y-3">
                                        {details.route_deductions.map((rd, idx) => (
                                            <div key={idx} className="bg-white rounded border border-orange-100 p-3">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-bold text-sm text-gray-800">
                                                        Ruta #{rd.route_number} — {rd.route_date ? new Date(rd.route_date).toLocaleDateString('es-CO') : '-'}
                                                    </span>
                                                    <span className="font-bold text-orange-700">
                                                        -{formatCurrency(rd.total_deduction)}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                                    <div>
                                                        <span className="text-gray-500 block">Gastos Aprobados</span>
                                                        <span className="font-medium">{formatCurrency(rd.approved_expenses)}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 block">Gastos Adicionales</span>
                                                        <span className="font-medium">{formatCurrency(rd.additional_expense)}</span>
                                                        {rd.additional_expense_desc && (
                                                            <span className="block text-gray-400 italic">{rd.additional_expense_desc}</span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 block">Sobrante/Faltante Real</span>
                                                        <span className={`font-medium ${rd.real_difference < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                            {formatCurrency(rd.real_difference)}
                                                        </span>
                                                        <div className="text-[9px] text-gray-400 mt-0.5">
                                                            Precisión conteo: {formatCurrency(rd.cash_difference)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 block">Recibido vs Esperado</span>
                                                        <span className="font-medium">
                                                            {formatCurrency(rd.received_cash)} / {formatCurrency(rd.expected_cash)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ⏰ Liquidaciones Tardías — rutas cuyo efectivo entró en esta sesión pero se liquidaron después del cierre */}
                            {details.late_route_settlements && details.late_route_settlements.length > 0 && (
                                <div className="border rounded-lg p-4 bg-amber-50 border-amber-300">
                                    <h3 className="font-bold text-amber-800 mb-1 flex items-center gap-2">
                                        <span>⏰</span> Liquidaciones Tardías
                                        <span className="text-sm font-normal text-amber-600">
                                            — Explican el descuadre de esta sesión
                                        </span>
                                    </h3>
                                    <p className="text-xs text-amber-700 mb-3">
                                        Estas rutas recaudaron efectivo en esta sesión pero se liquidaron <strong>después del cierre</strong>.
                                        Los gastos del mensajero nunca llegaron a caja, causando el faltante reflejado en la diferencia.
                                    </p>
                                    <div className="space-y-3">
                                        {details.late_route_settlements.map((ls, idx) => {
                                            const fmtN = (v) => new Intl.NumberFormat('es-CO').format(Math.round(v || 0));
                                            const gastos = (ls.additional_expense || 0) + (ls.approved_expenses || 0);
                                            return (
                                                <div key={idx} className="bg-white rounded border border-amber-200 p-3">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="font-bold text-sm text-gray-800">
                                                            Ruta #{ls.route_number} — {ls.route_date ? new Date(ls.route_date).toLocaleDateString('es-CO') : '-'}
                                                        </span>
                                                        <span className="text-xs text-amber-600 italic">
                                                            Liquidada: {ls.settled_at ? new Date(ls.settled_at).toLocaleString('es-CO') : '-'}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                                        <div>
                                                            <span className="text-gray-500 block">Efectivo en esta sesión</span>
                                                            <span className="font-bold text-green-700">$ {fmtN(ls.route_income_in_session)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500 block">Cajera recibió</span>
                                                            <span className="font-medium">$ {fmtN(ls.received_cash)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500 block">Gastos del mensajero</span>
                                                            <span className="font-bold text-red-600">-$ {fmtN(gastos)}</span>
                                                            {ls.additional_expense_desc && (
                                                                <span className="block text-gray-400 italic">{ls.additional_expense_desc}</span>
                                                            )}
                                                        </div>
                                                        <div className="bg-amber-100 rounded p-1.5">
                                                            <span className="text-gray-500 block">Faltante generado</span>
                                                            <span className="font-black text-amber-800">-$ {fmtN(gastos)}</span>
                                                            <span className="block text-[9px] text-amber-600">Efectivo que faltó en esta sesión</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-3 bg-amber-100 rounded p-2 text-xs text-amber-800">
                                        <strong>💡 Total explicado: -$ {
                                            new Intl.NumberFormat('es-CO').format(
                                                details.late_route_settlements.reduce((sum, ls) =>
                                                    sum + (ls.additional_expense || 0) + (ls.approved_expenses || 0), 0
                                                )
                                            )
                                        }</strong>
                                    </div>
                                </div>
                            )}

                            {/* Closing Info */}
                            {details.session.status === 'closed' && (
                                <div className="border rounded-lg p-4 bg-gray-50">
                                    <h3 className="font-bold text-gray-800 mb-4">Resumen del Cierre</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                        <div>
                                            <div className="text-sm text-gray-500">Saldo Esperado</div>
                                            <div className="font-semibold text-lg">
                                                {formatCurrency(details.session.calculated_expected_balance || details.session.closing_balance_expected)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-500">Saldo Declarado</div>
                                            <div className="font-semibold text-lg text-blue-600">
                                                {formatCurrency(details.session.closing_balance_declared)}
                                            </div>
                                        </div>
                                        <div className={`${parseFloat(details.session.difference) !== 0 ? 'bg-yellow-100 px-2 rounded -ml-2' : ''}`}>
                                            <div className="text-sm text-gray-500">Diferencia</div>
                                            <div className={`font-bold text-lg ${(parseFloat(details.session.closing_balance_declared) - parseFloat(details.session.calculated_expected_balance || details.session.closing_balance_expected)) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                {formatCurrency(parseFloat(details.session.closing_balance_declared) - parseFloat(details.session.calculated_expected_balance || details.session.closing_balance_expected))}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-500">Cerrado Por</div>
                                            <div>{details.session.closed_by_name}</div>
                                            <div className="text-xs text-gray-400">{formatDate(details.session.closed_at)}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Movements Tables */}
                            <div className="flex flex-col gap-12">
                                {/* Incomes */}
                                <div>
                                    <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                                        <span>⬇️</span> Ingresos
                                    </h3>

                                    {/* Ruta Sales */}
                                    {details.movements.filter(m => m.type === 'sale_income' && m.source === 'ruta').length > 0 && (
                                        <div className="mb-4">
                                            <div className="text-xs font-bold text-blue-700 uppercase mb-1 ml-1">🚚 Pilar 2: Recaudos de Ruta</div>
                                            <div className="border border-green-100 rounded-lg overflow-hidden mb-2 overflow-x-auto">
                                                <table className="w-full text-xs text-left whitespace-nowrap">
                                                    <thead className="bg-green-50 text-green-800 border-b border-green-100">
                                                        <tr>
                                                            <th className="py-1 px-2">#</th>
                                                            <th className="py-1 px-2">Ruta</th>
                                                            <th className="py-1 px-2">Pedido</th>
                                                            <th className="py-1 px-2">Cliente</th>
                                                            <th className="py-1 px-2">Fechas</th>
                                                            <th className="py-1 px-2 text-right">Valores</th>
                                                            <th className="py-1 px-2">Métodos</th>
                                                            <th className="py-1 px-2 text-center">RC</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-green-50">
                                                        {details.movements
                                                            .filter(m => m.type === 'sale_income' && m.source === 'ruta')
                                                            .map((m, idx) => {
                                                                let rcInfo = null;
                                                                if (m.siigo_payment_info) {
                                                                    try {
                                                                        const parsed = JSON.parse(m.siigo_payment_info);
                                                                        if (Array.isArray(parsed) && parsed.length > 0) rcInfo = parsed[0];
                                                                    } catch (e) { }
                                                                }
                                                                const intOrder = m.int_order_number;

                                                                return (
                                                                    <tr key={m.id} className="hover:bg-green-50/50">
                                                                        <td className="p-2 text-gray-500 w-8">{idx + 1}</td>
                                                                        <td className="p-2 text-center">
                                                                            {m.route_number ? (
                                                                                <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded font-bold text-[10px]">#{m.route_number}</span>
                                                                            ) : (
                                                                                <span className="text-gray-400">-</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="p-2">
                                                                            <div className="font-bold text-gray-900">{m.order_number || m.concept}</div>
                                                                            {intOrder && <div className="text-purple-600 text-[10px]">🔗 {intOrder}</div>}
                                                                        </td>
                                                                        <td className="p-2 max-w-[120px] truncate" title={m.beneficiary || m.customer_name}>
                                                                            {m.beneficiary || m.customer_name}
                                                                        </td>
                                                                        <td className="p-2 text-[10px] text-gray-500 leading-tight">
                                                                            <div>Fac: {m.invoice_date ? new Date(m.invoice_date).toLocaleDateString('es-CO') : '-'}</div>
                                                                            <div>Rec: {m.created_at ? new Date(m.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                                                                        </td>
                                                                        <td className="p-2 text-right font-mono text-green-700 font-medium">
                                                                            <div>{formatCurrency(m.amount)}</div>
                                                                            {m.int_total_amount > 0 && <div className="text-[10px] text-purple-600">Int: {formatCurrency(m.int_total_amount)}</div>}
                                                                        </td>
                                                                        <td className="p-2 text-[10px] leading-tight">
                                                                            <div>💳 {m.payment_method}</div>
                                                                            <div className="text-gray-500">📦 {m.delivery_method}</div>
                                                                        </td>
                                                                        <td className="p-2 text-center">
                                                                            {rcInfo ? (
                                                                                <span className="inline-block px-1 py-0.5 bg-green-100 text-green-800 rounded text-[10px]">
                                                                                    ✅ {rcInfo.name || rcInfo.number}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="inline-block px-1 py-0.5 bg-orange-100 text-orange-800 rounded text-[10px]">
                                                                                    ⏳ Pendiente
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="bg-green-100 border-t-2 border-green-300">
                                                            <td colSpan="5" className="p-2 text-xs font-bold text-green-900 text-right">
                                                                Total Ruta ({details.movements.filter(m => m.type === 'sale_income' && m.source === 'ruta').length} registros)
                                                            </td>
                                                            <td className="p-2 text-right font-mono text-green-900 font-bold text-sm">
                                                                {formatCurrency(details.movements.filter(m => m.type === 'sale_income' && m.source === 'ruta').reduce((sum, m) => sum + parseFloat(m.amount || 0), 0))}
                                                            </td>
                                                            <td colSpan="2"></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Bodega Sales */}
                                    {details.movements.filter(m => m.type === 'sale_income' && m.source === 'bodega').length > 0 && (
                                        <div className="mb-4">
                                            <div className="text-xs font-bold text-green-700 uppercase mb-1 ml-1">📦 Pilar 1: Recaudos de Bodega</div>
                                            <div className="border border-green-100 rounded-lg overflow-hidden mb-2 overflow-x-auto">
                                                <table className="w-full text-xs text-left whitespace-nowrap">
                                                    <thead className="bg-green-50 text-green-800 border-b border-green-100">
                                                        <tr>
                                                            <th className="py-1 px-2">#</th>
                                                            <th className="py-1 px-2">Pedido</th>
                                                            <th className="py-1 px-2">Cliente</th>
                                                            <th className="py-1 px-2">Fechas</th>
                                                            <th className="py-1 px-2 text-right">Valores</th>
                                                            <th className="py-1 px-2">Métodos</th>
                                                            <th className="py-1 px-2">Entregado Por</th>
                                                            <th className="py-1 px-2 text-center">RC</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-green-50">
                                                        {details.movements
                                                            .filter(m => m.type === 'sale_income' && m.source === 'bodega')
                                                            .map((m, idx) => {
                                                                let rcInfo = null;
                                                                if (m.siigo_payment_info) {
                                                                    try {
                                                                        const parsed = JSON.parse(m.siigo_payment_info);
                                                                        if (Array.isArray(parsed) && parsed.length > 0) rcInfo = parsed[0];
                                                                    } catch (e) { }
                                                                }
                                                                const intOrder = m.int_order_number;

                                                                return (
                                                                    <tr key={m.id} className="hover:bg-green-50/50">
                                                                        <td className="p-2 text-gray-500 w-8">{idx + 1}</td>
                                                                        <td className="p-2">
                                                                            <div className="font-bold text-gray-900">{m.order_number || m.concept}</div>
                                                                            {intOrder && <div className="text-purple-600 text-[10px]">🔗 {intOrder}</div>}
                                                                        </td>
                                                                        <td className="p-2 max-w-[120px] truncate" title={m.beneficiary || m.customer_name}>
                                                                            {m.beneficiary || m.customer_name}
                                                                        </td>
                                                                        <td className="p-2 text-[10px] text-gray-500 leading-tight">
                                                                            <div>Fac: {m.invoice_date ? new Date(m.invoice_date).toLocaleDateString('es-CO') : '-'}</div>
                                                                            <div>Rec: {m.created_at ? new Date(m.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                                                                        </td>
                                                                        <td className="p-2 text-right font-mono text-green-700 font-medium">
                                                                            <div>{formatCurrency(m.amount)}</div>
                                                                            {m.int_total_amount > 0 && <div className="text-[10px] text-purple-600">Int: {formatCurrency(m.int_total_amount)}</div>}
                                                                        </td>
                                                                        <td className="p-2 text-[10px] leading-tight">
                                                                            <div>💳 {m.payment_method || 'efectivo'}</div>
                                                                            <div className="text-gray-500">📦 {m.delivery_method || '-'}</div>
                                                                        </td>
                                                                        <td className="p-2 text-xs">
                                                                            {m.delivered_by || 'Bodega'}
                                                                        </td>
                                                                        <td className="p-2 text-center">
                                                                            {rcInfo ? (
                                                                                <span className="inline-block px-1 py-0.5 bg-green-100 text-green-800 rounded text-[10px]">
                                                                                    ✅ {rcInfo.name || rcInfo.number}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="inline-block px-1 py-0.5 bg-orange-100 text-orange-800 rounded text-[10px]">
                                                                                    ⏳ Pendiente
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="bg-green-100 border-t-2 border-green-300">
                                                            <td colSpan="5" className="p-2 text-xs font-bold text-green-900 text-right">
                                                                Total Bodega ({details.movements.filter(m => m.type === 'sale_income' && m.source === 'bodega').length} registros)
                                                            </td>
                                                            <td className="p-2 text-right font-mono text-green-900 font-bold text-sm">
                                                                {formatCurrency(details.movements.filter(m => m.type === 'sale_income' && m.source === 'bodega').reduce((sum, m) => sum + parseFloat(m.amount || 0), 0))}
                                                            </td>
                                                            <td colSpan="2"></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Pilar 3: Cartera (Portfolio Payments) */}
                                    {details.portfolio_payments && details.portfolio_payments.length > 0 && (
                                        <div className="mb-4">
                                            <div className="text-xs font-bold text-indigo-700 uppercase mb-1 ml-1">💼 Pilar 3: Cartera (Abonos Efectivo)</div>
                                            <div className="border border-indigo-100 rounded-lg overflow-hidden mb-2 overflow-x-auto">
                                                <table className="w-full text-xs text-left whitespace-nowrap">
                                                    <thead className="bg-indigo-50 text-indigo-800 border-b border-indigo-100">
                                                        <tr>
                                                            <th className="py-1 px-2">#</th>
                                                            <th className="py-1 px-2">Documento</th>
                                                            <th className="py-1 px-2">Cliente</th>
                                                            <th className="py-1 px-2 text-right">Monto</th>
                                                            <th className="py-1 px-2">Gestor</th>
                                                            <th className="py-1 px-2 text-center">RC</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-indigo-50">
                                                        {details.portfolio_payments.map((p, idx) => (
                                                            <tr key={p.id} className="hover:bg-indigo-50/50">
                                                                <td className="p-2 text-gray-500 w-8">{idx + 1}</td>
                                                                <td className="p-2">
                                                                    <div className="font-bold text-gray-900">{p.order_number}</div>
                                                                    <div className="text-[10px] text-indigo-500">
                                                                        {new Date(p.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                                    </div>
                                                                </td>
                                                                <td className="p-2 max-w-[120px] truncate" title={p.customer_name}>
                                                                    {p.customer_name}
                                                                </td>
                                                                <td className="p-2 text-right font-mono text-indigo-700 font-medium">
                                                                    {formatCurrency(p.amount)}
                                                                </td>
                                                                <td className="p-2 text-[10px] text-gray-600">
                                                                    👤 {p.managed_by_name || p.managed_by_username || 'Sistema'}
                                                                </td>
                                                                <td className="p-2 text-center">
                                                                    {p.has_rc ? (
                                                                        <span className="inline-block px-1 py-0.5 bg-green-100 text-green-800 rounded text-[10px]">
                                                                            ✅ {p.rc_number}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-block px-1 py-0.5 bg-orange-100 text-orange-800 rounded text-[10px]">
                                                                            ⏳ Pendiente
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="bg-indigo-100 border-t-2 border-indigo-300">
                                                            <td colSpan="3" className="p-2 text-xs font-bold text-indigo-900 text-right">
                                                                Total Cartera ({details.portfolio_payments.length} abonos)
                                                            </td>
                                                            <td className="p-2 text-right font-mono text-indigo-900 font-bold text-sm">
                                                                {formatCurrency(details.portfolio_payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))}
                                                            </td>
                                                            <td colSpan="2"></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Other Income */}
                                    {details.movements.filter(m => m.type === 'extra_income').length > 0 && (
                                        <div className="mb-4">
                                            <div className="text-xs font-bold text-green-700 uppercase mb-1 ml-1">Otros Ingresos</div>
                                            <div className="border border-green-100 rounded-lg overflow-hidden">
                                                <table className="w-full text-sm text-left">
                                                    <tbody className="divide-y divide-green-50">
                                                        {details.movements
                                                            .filter(m => m.type === 'extra_income')
                                                            .map(m => (
                                                                <tr key={m.id} className="hover:bg-green-50/50">
                                                                    <td className="p-2 text-xs w-20">{formatDate(m.created_at).split(' ')[1]} {formatDate(m.created_at).split(' ')[2]}</td>
                                                                    <td className="p-2">
                                                                        <div className="font-medium text-gray-900">{m.concept}</div>
                                                                        {m.beneficiary && <div className="text-xs text-gray-500">{m.beneficiary}</div>}
                                                                    </td>
                                                                    <td className="p-2 text-right font-mono text-green-700 font-medium w-24">
                                                                        {formatCurrency(m.amount)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {details.movements.filter(m => ['extra_income', 'sale_income'].includes(m.type)).length === 0 && (
                                        <div className="text-gray-400 italic text-sm p-4 border border-dashed rounded text-center">No hay ingresos registrados</div>
                                    )}
                                </div>

                                {/* Expenses */}
                                <div>
                                    <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                                        <span>⬆️</span> Egresos
                                    </h3>
                                    {(() => {
                                        const expenseMovements = details.movements.filter(m => !['extra_income', 'sale_income'].includes(m.type));
                                        const totalExpenses = expenseMovements.reduce((sum, m) => sum + parseFloat(m.amount || 0), 0);
                                        return expenseMovements.length > 0 ? (
                                            <div className="border border-red-100 rounded-lg overflow-hidden">
                                                <table className="w-full text-sm text-left">
                                                    <thead className="bg-red-50 text-red-800 font-medium">
                                                        <tr>
                                                            <th className="p-2">Hora</th>
                                                            <th className="p-2">Concepto</th>
                                                            <th className="p-2 text-right">Monto</th>
                                                            <th className="p-2 text-center">Soporte</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-red-50">
                                                        {expenseMovements.map(m => (
                                                            <tr key={m.id} className="hover:bg-red-50/50">
                                                                <td className="p-2 text-xs">{formatDate(m.created_at).split(' ')[1]} {formatDate(m.created_at).split(' ')[2]}</td>
                                                                <td className="p-2">
                                                                    <div className="font-medium text-gray-900">{m.concept}</div>
                                                                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700 font-medium inline-block mb-0.5">
                                                                        {m.type}
                                                                    </span>
                                                                    {m.beneficiary && <div className="text-xs text-gray-500 mt-0.5">{m.beneficiary}</div>}
                                                                </td>
                                                                <td className="p-2 text-right font-mono text-red-700 font-medium">
                                                                    {formatCurrency(m.amount)}
                                                                </td>
                                                                <td className="p-2 text-center">
                                                                    {m.evidence_file ? (
                                                                        <button
                                                                            onClick={() => window.open(`/${m.evidence_file}`, '_blank')}
                                                                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-[10px] font-medium transition-colors"
                                                                            title="Ver soporte"
                                                                        >
                                                                            <ImageIcon size={12} /> Ver
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-[10px] text-gray-400 italic">Sin soporte</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="bg-red-100 border-t-2 border-red-300">
                                                            <td colSpan="2" className="p-2 text-xs font-bold text-red-900 text-right">
                                                                Total Egresos ({expenseMovements.length} registros)
                                                            </td>
                                                            <td className="p-2 text-right font-mono text-red-900 font-bold text-sm">
                                                                {formatCurrency(totalExpenses)}
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-gray-400 italic text-sm p-4 border border-dashed rounded text-center">No hay egresos registrados</div>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Cash Distribution - Base Final + Guardado */}
                            {details.session.status === 'closed' && (() => {
                                const openingBalance = parseFloat(details.session.opening_balance || 0);
                                const baseLeft = parseFloat(details.session.base_left || 0);
                                const declared = parseFloat(details.session.closing_balance_declared || 0);
                                const sentToSafe = declared - baseLeft;

                                return (
                                    <div className="border-2 border-indigo-200 rounded-xl p-5 bg-gradient-to-r from-indigo-50 to-purple-50">
                                        <h3 className="font-bold text-indigo-900 mb-4 text-base flex items-center gap-2">
                                            💰 Distribución del Efectivo
                                        </h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-white rounded-lg p-3 border border-indigo-100 shadow-sm">
                                                <div className="text-[10px] text-gray-500 uppercase font-bold">Saldo Declarado</div>
                                                <div className="text-lg font-black text-gray-900">{formatCurrency(declared)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-3 border border-blue-200 shadow-sm">
                                                <div className="text-[10px] text-blue-600 uppercase font-bold">📤 Enviado a Guardar</div>
                                                <div className="text-lg font-black text-blue-700">{formatCurrency(sentToSafe > 0 ? sentToSafe : 0)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-3 border border-green-200 shadow-sm">
                                                <div className="text-[10px] text-green-600 uppercase font-bold">🏦 Base para Mañana</div>
                                                <div className="text-lg font-black text-green-700">{formatCurrency(baseLeft)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                                                <div className="text-[10px] text-gray-500 uppercase font-bold">Base de Hoy (Ref)</div>
                                                <div className="text-lg font-black text-gray-600">{formatCurrency(openingBalance)}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Notes */}
                            {details.session.notes && (
                                <div>
                                    <h3 className="font-bold text-gray-800 mb-2">Notas del Cierre</h3>
                                    <div className="bg-yellow-50 p-4 rounded text-sm text-gray-700 whitespace-pre-wrap">
                                        {details.session.notes}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-red-500 text-center">No se encontraron detalles</div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 flex justify-between">
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowReport(true)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white font-bold flex items-center gap-2"
                            disabled={loading || !details}
                        >
                            <span>📄</span> Ver Reporte
                        </button>
                        <button
                            onClick={handleExport}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-white font-bold flex items-center gap-2"
                            disabled={loading || !details}
                        >
                            <span>📊</span> CSV
                        </button>
                        <button
                            onClick={handleExportPDF}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-white font-bold flex items-center gap-2"
                            disabled={loading || !details}
                        >
                            <span>📄</span> PDF
                        </button>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800 font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionDetailsModal;
